import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Collapse,
  Tabs,
  Tab,
  Select,
  FormControl,
  InputLabel,
  TablePagination
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as ResolvedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Email as EmailIcon,
  Storage as DatabaseIcon,
  Api as ApiIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';

interface ErrorLog {
  id: number;
  error_level: string;
  error_type: string;
  message: string;
  file: string;
  line: number;
  user_id: number | null;
  user_email: string | null;
  customer_id: number | null;
  customer_name: string | null;
  ip_address: string;
  request_uri: string;
  context: string | null;
  stack_trace: string | null;
  created_at: string;
  resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution: string | null;
}

interface ErrorStats {
  total_errors: number;
  critical_count: number;
  error_count: number;
  warning_count: number;
  by_type: Record<string, number>;
}

const IssuesTracking: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [resolveDialog, setResolveDialog] = useState(false);
  const [resolution, setResolution] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    type: '',
    level: '',
    resolved: 'unresolved'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchErrors();
    fetchStats();
  }, [filters]);

  const fetchErrors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.level) params.append('level', filters.level);
      params.append('limit', '500');

      const response = await api.get(`/issues-tracking?${params}`);
      let errorList = response.data.errors || [];
      
      // Filter by resolved status
      if (filters.resolved === 'resolved') {
        errorList = errorList.filter((e: ErrorLog) => e.resolved);
      } else if (filters.resolved === 'unresolved') {
        errorList = errorList.filter((e: ErrorLog) => !e.resolved);
      }
      
      setErrors(errorList);
    } catch (error) {
      console.error('Failed to fetch errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/issues-tracking?action=stats&days=7');
      setStats(response.data.summary);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleResolve = async () => {
    if (!selectedError || !resolution) return;

    try {
      await api.put('/issues-tracking', {
        error_id: selectedError.id,
        resolution
      });
      
      setResolveDialog(false);
      setResolution('');
      setSelectedError(null);
      fetchErrors();
    } catch (error) {
      console.error('Failed to resolve error:', error);
    }
  };

  const toggleRowExpansion = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'critical':
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EmailIcon />;
      case 'database':
        return <DatabaseIcon />;
      case 'api':
        return <ApiIcon />;
      case 'security':
        return <SecurityIcon />;
      default:
        return <BugIcon />;
    }
  };

  const getLevelColor = (level: string): "error" | "warning" | "info" | "success" | "default" => {
    switch (level) {
      case 'critical':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'notice':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Issues Tracking</Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchErrors();
            fetchStats();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Issues (7 days)
                </Typography>
                <Typography variant="h4">
                  {stats.total_errors}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderLeft: '4px solid', borderColor: 'error.main' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Critical/Errors
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.critical_count + stats.error_count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Warnings
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.warning_count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Most Common Type
                </Typography>
                <Typography variant="h6">
                  {Object.entries(stats.by_type).sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Error Type</InputLabel>
              <Select
                value={filters.type}
                label="Error Type"
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="database">Database</MenuItem>
                <MenuItem value="api">API</MenuItem>
                <MenuItem value="security">Security</MenuItem>
                <MenuItem value="system">System</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Level</InputLabel>
              <Select
                value={filters.level}
                label="Level"
                onChange={(e) => setFilters({ ...filters, level: e.target.value })}
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="notice">Notice</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.resolved}
                label="Status"
                onChange={(e) => setFilters({ ...filters, resolved: e.target.value })}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unresolved">Unresolved</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Errors Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50"></TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((error) => (
              <React.Fragment key={error.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(error.id)}
                    >
                      {expandedRows.has(error.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getLevelIcon(error.error_level)}
                      label={error.error_level}
                      color={getLevelColor(error.error_level)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTypeIcon(error.error_type)}
                      {error.error_type}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 400 }} noWrap>
                      {error.message}
                    </Typography>
                  </TableCell>
                  <TableCell>{error.customer_name || '-'}</TableCell>
                  <TableCell>
                    {format(new Date(error.created_at), 'MMM dd HH:mm')}
                  </TableCell>
                  <TableCell>
                    {error.resolved ? (
                      <Chip
                        icon={<ResolvedIcon />}
                        label="Resolved"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip label="Open" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {!error.resolved && (
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedError(error);
                          setResolveDialog(true);
                        }}
                      >
                        Resolve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 0 }}>
                    <Collapse in={expandedRows.has(error.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Details
                            </Typography>
                            <Typography variant="body2">
                              <strong>File:</strong> {error.file}:{error.line}
                            </Typography>
                            <Typography variant="body2">
                              <strong>User:</strong> {error.user_email || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>IP:</strong> {error.ip_address}
                            </Typography>
                            <Typography variant="body2">
                              <strong>URI:</strong> {error.request_uri}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            {error.resolved && (
                              <Alert severity="success">
                                <Typography variant="body2">
                                  <strong>Resolved by:</strong> {error.resolved_by}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Resolution:</strong> {error.resolution}
                                </Typography>
                              </Alert>
                            )}
                            {error.context && (
                              <Box mt={2}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Context
                                </Typography>
                                <pre style={{ fontSize: '0.875rem', overflow: 'auto' }}>
                                  {JSON.stringify(JSON.parse(error.context), null, 2)}
                                </pre>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={errors.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialog}
        onClose={() => setResolveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve Issue</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Resolution"
            placeholder="Describe how this issue was resolved..."
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolve}
            disabled={!resolution}
          >
            Mark as Resolved
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IssuesTracking;